import { createFileRoute } from '@tanstack/react-router';
import { CTASection, FeatureGrid, Hero, Section, Steps } from '@cartcloser/ui/marketing';

export const Route = createFileRoute('/how-it-works')({
  component: HowItWorks,
});

interface Channel {
  name: string;
  included: boolean;
  comingSoon?: boolean;
  description: string;
}

const CHANNELS: Channel[] = [
  {
    name: 'Live chat',
    included: true,
    description:
      'Specialist agents handle high-intent chat on your storefront — qualifying, answering spec-deep questions, and recommending the right products.',
  },
  {
    name: 'Phone',
    included: true,
    comingSoon: true,
    description:
      'The same agents answer sales calls, so phone shoppers get an expert too — not a queue or an order-taker.',
  },
  {
    name: 'Email',
    included: false,
    description:
      'Deliberately not a CartCloser channel. Inbound sales email is a follow-up, not a closing channel — it doesn’t convert. Your team keeps email.',
  },
];

const STEPS = [
  {
    title: '1 · The Receptionist routes',
    description:
      'A lightweight agent reads the shopper’s opening question and routes them to the single best expert by category — Dishwashers, Fridges, Prams, whatever you sell.',
  },
  {
    title: '2 · The expert qualifies & pivots',
    description:
      'The category expert asks 2–3 sharp questions, then adapts in real time as the shopper reveals constraints — width, budget, noise, family size.',
  },
  {
    title: '3 · It recommends from your catalogue',
    description:
      'It presents the best 2–3 products — only your SKUs — each with a one-line reason tied to what the shopper said.',
  },
];

const GROUNDING = [
  {
    title: 'Grounded in your specs',
    description:
      'Upload product feeds and PDF spec sheets. We extract structured specs and topic knowledge once, at upload — so answers are precise and instant.',
  },
  {
    title: 'Scoped to your SKUs',
    description:
      'Each agent recommends only from its categories (minus any blacklist) or an explicit whitelist. It never invents products or surfaces competitors.',
  },
  {
    title: 'Custom agent prompts',
    description:
      'Agents start from proven templates you can augment or overwrite per field — your tone, policies and promotions, without losing the playbook.',
  },
];

function HowItWorks() {
  return (
    <div>
      <Hero
        eyebrow="Your AI sales contact centre"
        title="Augment — or replace — your contact centre for inbound sales"
        subtitle="CartCloser staffs your two highest-converting channels — live chat and phone — with specialist agents that sell. Not generic support. Not email."
        primaryCta={{ label: 'See a live demo', href: '/demos' }}
        secondaryCta={{ label: 'Contact', href: '/contact' }}
      />

      <Section
        eyebrow="Channels"
        title="Built for the channels that close"
        subtitle="High-intent shoppers convert on chat and phone. CartCloser covers both — and intentionally skips email."
      >
        <div className="grid gap-6 md:grid-cols-3">
          {CHANNELS.map((c) => (
            <div
              key={c.name}
              className={
                c.included
                  ? 'rounded-2xl border border-primary/30 bg-card p-6 shadow-brand'
                  : 'rounded-2xl border border-dashed border-border bg-muted/30 p-6'
              }
            >
              <div className="mb-2 flex items-center gap-2">
                <span
                  className={
                    c.included
                      ? 'flex h-6 w-6 items-center justify-center rounded-full bg-primary text-sm text-primary-foreground'
                      : 'flex h-6 w-6 items-center justify-center rounded-full bg-secondary text-sm text-muted-foreground'
                  }
                  aria-hidden
                >
                  {c.included ? '✓' : '✕'}
                </span>
                <h3 className="font-semibold">{c.name}</h3>
                <span className="ml-auto text-xs font-medium text-muted-foreground">
                  {c.included ? (c.comingSoon ? 'Coming soon' : 'Included') : 'By design'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{c.description}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="The 3-step sales playbook">
        <Steps steps={STEPS} />
      </Section>

      <Section
        eyebrow="Under the hood"
        title="Accurate because it’s grounded in your data"
        subtitle="No hallucinated products, no stale specs — the agent only knows what you give it, pre-processed for fast, precise answers."
      >
        <FeatureGrid features={GROUNDING} className="mt-0 py-0" />
      </Section>

      <CTASection
        title="See it on your own catalogue"
        subtitle="Import your catalogue, pick an agent template, embed one script tag."
        cta={{ label: 'Contact', href: '/contact' }}
      />
    </div>
  );
}
